import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Google OAuth callback page.
 * Reached after the user grants permission on Google's consent screen.
 * Google redirects here with `?code=...&state=...`.
 * We POST the code to our backend, which exchanges it for tokens + returns a JWT.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function GoogleCallback() {
  const navigate = useNavigate();
  const { setUser, setToken } = useAuth();
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const error = params.get('error');

    if (error) {
      navigate('/auth?error=' + encodeURIComponent(error));
      return;
    }
    if (!code) {
      navigate('/auth');
      return;
    }

    const expectedState = sessionStorage.getItem('google_oauth_state');
    if (expectedState && state && expectedState !== state) {
      console.error('OAuth state mismatch');
      navigate('/auth?error=state_mismatch');
      return;
    }

    const redirectUri = window.location.origin + '/auth/google';

    (async () => {
      try {
        const res = await axios.post(`${API}/auth/google/exchange`, {
          code,
          redirect_uri: redirectUri,
        }, { withCredentials: true });
        const { token, user } = res.data || {};
        if (!token || !user) throw new Error('Invalid response');
        localStorage.setItem('auth_token', token);
        if (setToken) setToken(token);
        setUser(user);
        try { sessionStorage.removeItem('google_oauth_state'); } catch {}
        // Clean URL (remove ?code=...) and go to dashboard
        window.history.replaceState({}, '', '/dashboard');
        navigate('/dashboard', { replace: true });
      } catch (err) {
        console.error('Google exchange failed', err);
        navigate('/auth?error=google_exchange_failed');
      }
    })();
  }, [navigate, setUser, setToken]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F9F7]" data-testid="google-callback">
      <div className="text-center">
        <div className="w-10 h-10 border-2 border-[#1A3C34] border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="mt-4 text-sm text-[#737373]">Connexion en cours…</p>
      </div>
    </div>
  );
}
